import React, { useEffect, useRef, useState } from "react";
import "./StoryTellingModal.css";
import { Chart } from "chart.js/auto";
import jsPDF from "jspdf";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";

import { useTranslation } from "react-i18next";

import { getLayerAttributes } from "../../../../_config/layers/zcm";

const StoryTellingModal = ({ active, callback, map }) => {
    const { t } = useTranslation();


    const [show, setShow] = useState(false);

    const [layerOptions, setLayerOptions] = useState([]);

    const [layerName, setLayerName] = useState("");

    const [sectionType, setSectionType] = useState("groupBy");

    const [sectionTitle, setSectionTitle] = useState("");

    const [sectionDescription, setSectionDescription] = useState("");

    const [fieldX, setFieldX] = useState("");

    const [fieldY, setFieldY] = useState("");

    const [fieldZ, setFieldZ] = useState("");

    const [operator, setOperator] = useState("=");

    const [filterValue, setFilterValue] = useState("");

    const [chartType, setChartType] = useState("line");

    const [attributes, setAttributes] = useState([]);

    const [attributeTypes, setAttributeTypes] = useState([]);

    const [loadingAttributes, setLoadingAttributes] = useState(false);

    const [loadingSection, setLoadingSection] = useState(false);

    const [sections, setSections] = useState([]);

    const chartsRef = useRef({});

    const sectionTypeOptions = [
        {
            value: "groupBy",
            label: t("storyModal.sectionType.groupBy"),
            icon: "pi pi-sort-amount-down",
        },
        {
            value: "customQuery",
            label: t("storyModal.sectionType.customQuery"),
            icon: "pi pi-filter",
        },
        {
            value: "percentResult",
            label: t("storyModal.sectionType.percent"),
            icon: "pi pi-percentage",
        },
        {
            value: "countResult",
            label: t("storyModal.sectionType.count"),
            icon: "pi pi-hashtag",
        },
        {
            value: "averageResult",
            label: t("storyModal.sectionType.average"),
            icon: "pi pi-calculator",
        },
        {
            value: "graphResult",
            label: t("storyModal.sectionType.graph"),
            icon: "pi pi-chart-bar",
        },
    ];

    const operatorOptions = [
        { value: "=", label: "Igual a", symbol: "=" },
        { value: ">", label: "Maior que", symbol: ">" },
        { value: "<", label: "Menor que", symbol: "<" },
        { value: ">=", label: "Maior ou igual a", symbol: "≥" },
        { value: "<=", label: "Menor ou igual a", symbol: "≤" },
        { value: "!=", label: "Diferente de", symbol: "≠" },
    ];

    const chartTypeOptions = [
        {
            value: "line",
            label: t("statisticModal.chartType.line"),
            icon: "pi pi-chart-line",
        },
        {
            value: "bar",
            label: t("statisticModal.chartType.bar"),
            icon: "pi pi-chart-bar",
        },
        {
            value: "pie",
            label: t("statisticModal.chartType.pie"),
            icon: "pi pi-chart-pie",
        },
    ];

 

    const iconItemTemplate = (option) => {
        if (!option) return null;

        return (
            <div className="StoryOption">
                <i className={option.icon} />
                <span>{option.label}</span>
            </div>
        );
    };

    const iconValueTemplate = (option, props) => {
        if (!option) return <span>{props.placeholder}</span>;

        return iconItemTemplate(option);
    };

    const operatorItemTemplate = (option) => {
        if (!option) return null;

        return (
            <div className="StoryOption">
                <span className="StoryOperatorSymbol">{option.symbol}</span>
                <span>{option.label}</span>
            </div>
        );
    };

    const operatorValueTemplate = (option, props) => {
        if (!option) return <span>{props.placeholder}</span>;

        return operatorItemTemplate(option);
    };



    useEffect(() => {
        if (active) {
            setShow(true);
        }
    }, [active]);

    useEffect(() => {
        callback(show);
    }, [show, callback]);

    const handleClose = () => {
        setShow(false);
    };


    useEffect(() => {
        if (!map) {
            return;
        }

        const layers = map.getLayers().getArray();

        const options = layers
            .filter((layer) => layer.get("isAllowedToStatistics"))
            .map((layer) => {
                const wmsName = layer.get("wmsName");

                if (!wmsName) {
                    return null;
                }

                return {
                    value: wmsName.includes(":")
                        ? wmsName.split(":")[1]
                        : wmsName,

                    label: layer.get("title") || wmsName,
                };
            })
            .filter(Boolean);

        setLayerOptions(options);
    }, [map]);



    const resetFields = () => {
        setAttributes([]);
        setAttributeTypes([]);

        setFieldX("");
        setFieldY("");
        setFieldZ("");

        setOperator("=");

        setFilterValue("");

        setChartType("line");
    };

  

    const loadAttributes = async (selectedLayer) => {
        setLayerName(selectedLayer);

        resetFields();

        if (!selectedLayer) {
            return;
        }

        try {
            setLoadingAttributes(true);

            const response = await getLayerAttributes(selectedLayer);

            const wfsAttributes = response?.wfsAttributes;

            if (!wfsAttributes) {
                return;
            }

            const parsedAttributes =
                wfsAttributes
                    .match(/[\w]{1,}:/g)
                    ?.map((attr) => attr.slice(0, -1)) || [];

            const parsedTypes =
                wfsAttributes
                    .match(/[\w]{1,},/g)
                    ?.map((type) => type.slice(0, -1)) || [];

            setAttributes(parsedAttributes);

            setAttributeTypes(parsedTypes);

        

            const x = parsedAttributes.find(
                (attr, index) =>
                    isTextualOrDate(parsedTypes[index] || "") ||
                    attr.toLowerCase().includes("ano")
            );

            const y = parsedAttributes.find((_, index) =>
                isNumericOrDate(parsedTypes[index] || "")
            );

            setFieldX(x || "");
            setFieldY(y || "");
            setFieldZ(
                parsedAttributes.find((_, index) =>
                    isNumericOrDate(parsedTypes[index] || "")
                ) || ""
            );
        } catch (error) {
            console.error("Erro ao carregar os atributos:", error);
        } finally {
            setLoadingAttributes(false);
        }
    };



    const isTextualOrDate = (type) => {
        const normalized = String(type || "").toLowerCase();

        return [
            "character",
            "varchar",
            "text",
            "date",
            "timestamp",
            "time",
            "string",
        ].some((item) => normalized.startsWith(item));
    };

    const isNumericOrDate = (type) => {
        const normalized = String(type || "").toLowerCase();

        return [
            "integer",
            "int",
            "float",
            "double",
            "bigdecimal",
            "long",
            "numeric",
            "decimal",
            "date",
            "timestamp",
            "time",
        ].some((item) => normalized.startsWith(item));
    };



    const toOptions = (list) =>
        list.map((attr) => ({ label: attr, value: attr }));

    const fieldXOptions = toOptions(
        attributes.filter(
            (attr, index) =>
                isTextualOrDate(attributeTypes[index] || "") ||
                attr.toLowerCase().includes("ano")
        )
    );

    const fieldYOptions = toOptions(
        attributes.filter((_, index) =>
            isNumericOrDate(attributeTypes[index] || "")
        )
    );

    const fieldZOptions = toOptions(attributes);



    const showFieldX =
        sectionType === "groupBy" ||
        sectionType === "customQuery" ||
        sectionType === "graphResult";

    const showFieldY =
        sectionType === "groupBy" ||
        sectionType === "customQuery" ||
        sectionType === "graphResult";

    const showFieldZ =
        sectionType === "customQuery" ||
        sectionType === "percentResult" ||
        sectionType === "countResult" ||
        sectionType === "averageResult" ||
        sectionType === "graphResult";

    const showOperator =
        sectionType === "customQuery" ||
        sectionType === "percentResult" ||
        sectionType === "countResult" ||
        sectionType === "averageResult" ||
        sectionType === "graphResult";

    const showChartType = sectionType === "graphResult";

    const canAddSection =
        !!layerName &&
        !(showFieldX && !fieldX) &&
        !(showFieldY && !fieldY) &&
        !(showFieldZ && !fieldZ);


    const buildWfsUrl = (selectedLayer, filter = "") => {
        const baseUrl = "https://hgpedea.sema.ce.gov.br/geoserver/wfs";

        const params = new URLSearchParams();

        params.set("service", "WFS");

        params.set("version", "1.0.0");

        params.set("request", "GetFeature");

        params.set("typeName", selectedLayer);

        params.set("outputFormat", "application/json");

        if (filter) {
            params.set("CQL_FILTER", filter);
        }

        return `${baseUrl}?${params.toString()}`;
    };

    const fetchFeatures = async (filter = "") => {
        const response = await fetch(buildWfsUrl(layerName, filter));

        if (!response.ok) {
            throw new Error("Erro ao consultar o WFS.");
        }

        const data = await response.json();

        return data.features || [];
    };

 

    const evaluateCondition = (value, selectedOperator, selectedFilterValue) => {
        if (value === null || value === undefined) {
            return false;
        }

        const stringValue = String(value);

        const stringFilter = String(selectedFilterValue ?? "");

        const numericValue = Number(value);

        const numericFilter = Number(selectedFilterValue);

        const bothNumeric =
            !Number.isNaN(numericValue) && !Number.isNaN(numericFilter);

        const left = bothNumeric ? numericValue : stringValue;

        const right = bothNumeric ? numericFilter : stringFilter;

        switch (selectedOperator) {
            case "=":
                return left == right;

            case "!=":
                return left != right;

            case ">":
                return left > right;

            case "<":
                return left < right;

            case ">=":
                return left >= right;

            case "<=":
                return left <= right;

            default:
                return false;
        }
    };


    const aggregateData = (xValues, yValues) => {
        const aggregatedData = {};

        xValues.forEach((x, index) => {
            const key =
                x === null || x === undefined || x === ""
                    ? "Sem informação"
                    : String(x);

            if (!aggregatedData[key]) {
                aggregatedData[key] = 0;
            }

            aggregatedData[key] += Number(yValues[index]) || 0;
        });

        return {
            xValues: Object.keys(aggregatedData),

            yValues: Object.values(aggregatedData),
        };
    };

    const sortAggregatedData = (aggregatedData) => {
        const combined = aggregatedData.xValues.map((xValue, index) => ({
            x: xValue,
            y: aggregatedData.yValues[index],
        }));

        combined.sort((a, b) => b.y - a.y);

        return {
            xValues: combined.map((item) => item.x),

            yValues: combined.map((item) => item.y),
        };
    };

    const extractXY = (features, section) => {
        const xValues = [];
        const yValues = [];

        features.forEach((feature) => {
            xValues.push(feature.properties?.[section.fieldX]);

            yValues.push(feature.properties?.[section.fieldY]);
        });

        return { xValues, yValues };
    };

   

    const addSection = async (event) => {
        event.preventDefault();

        if (!layerName) {
            return;
        }

        if (
            (showFieldX && !fieldX) ||
            (showFieldY && !fieldY) ||
            (showFieldZ && !fieldZ)
        ) {
            return;
        }

        const sectionId = `${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 8)}`;

        const newSection = {
            id: sectionId,

            type: sectionType,

            title: sectionTitle.trim(),

            description: sectionDescription.trim(),

            fieldX,

            fieldY,

            fieldZ,

            operator,

            filterValue,

            chartType,

            status: "loading",

            data: null,
        };

        setSections((previous) => [...previous, newSection]);

        setLoadingSection(true);

        try {
            let result = null;

            switch (sectionType) {
                case "groupBy":
                    result = await generateGroupBy(newSection);
                    break;

                case "customQuery":
                    result = await generateCustomQuery(newSection);
                    break;

                case "percentResult":
                    result = await generatePercentResult(newSection);
                    break;

                case "countResult":
                    result = await generateCountResult(newSection);
                    break;

                case "averageResult":
                    result = await generateAverageResult(newSection);
                    break;

                case "graphResult":
                    result = await generateGraphResult(newSection);
                    break;

                default:
                    throw new Error("Tipo de seção inválido.");
            }

            setSections((previous) =>
                previous.map((section) =>
                    section.id === sectionId
                        ? { ...section, status: "success", data: result }
                        : section
                )
            );
        } catch (error) {
            console.error("Erro ao gerar seção:", error);

            setSections((previous) =>
                previous.map((section) =>
                    section.id === sectionId
                        ? { ...section, status: "error", data: null }
                        : section
                )
            );
        } finally {
            setLoadingSection(false);
        }
    };



    const generateGroupBy = async (section) => {
        const features = await fetchFeatures();

        const { xValues, yValues } = extractXY(features, section);

        const aggregated = sortAggregatedData(aggregateData(xValues, yValues));

        return {
            xValues: aggregated.xValues,

            yValues: aggregated.yValues,
        };
    };



    const generateCustomQuery = async (section) => {
        const filter = `${section.fieldZ} ${section.operator} '${section.filterValue}'`;

        const features = await fetchFeatures(filter);

        const { xValues, yValues } = extractXY(features, section);

        const aggregated = sortAggregatedData(aggregateData(xValues, yValues));

        return {
            xValues: aggregated.xValues,

            yValues: aggregated.yValues,
        };
    };


    const generatePercentResult = async (section) => {
        const features = await fetchFeatures();

        const totalRecords = features.length;

        const filteredRecords = features.filter((feature) =>
            evaluateCondition(
                feature.properties?.[section.fieldZ],
                section.operator,
                section.filterValue
            )
        ).length;

        const percentage =
            totalRecords > 0 ? (filteredRecords / totalRecords) * 100 : 0;

        return {
            percentage,
        };
    };



    const generateCountResult = async (section) => {
        const features = await fetchFeatures();

        const count = features.filter((feature) =>
            evaluateCondition(
                feature.properties?.[section.fieldZ],
                section.operator,
                section.filterValue
            )
        ).length;

        return {
            count,
        };
    };



    const generateAverageResult = async (section) => {
        const features = await fetchFeatures();

        const filteredRecords = features.filter((feature) =>
            evaluateCondition(
                feature.properties?.[section.fieldZ],
                section.operator,
                section.filterValue
            )
        );

        const total = filteredRecords.reduce(
            (sum, feature) =>
                sum + (Number(feature.properties?.[section.fieldZ]) || 0),
            0
        );

        const average =
            filteredRecords.length > 0 ? total / filteredRecords.length : 0;

        return {
            average,
        };
    };

  

    const generateGraphResult = async (section) => {
        let filter = "";

        if (section.fieldZ && section.operator && section.filterValue !== "") {
            filter = `${section.fieldZ} ${section.operator} '${section.filterValue}'`;
        }

        const features = await fetchFeatures(filter);

        const { xValues, yValues } = extractXY(features, section);

        const aggregated = aggregateData(xValues, yValues);

        return {
            xValues: aggregated.xValues,

            yValues: aggregated.yValues,
        };
    };

 

    useEffect(() => {
        sections.forEach((section) => {
            if (section.type !== "graphResult") {
                return;
            }

            if (section.status !== "success") {
                return;
            }

            const canvas = document.getElementById(`chart-${section.id}`);

            if (!canvas) {
                return;
            }

            if (chartsRef.current[section.id]) {
                return;
            }

            const data = section.data;

            if (!data || data.xValues.length === 0) {
                return;
            }

            const backgroundColors = generateRandomColors(data.xValues.length);

            const borderColors = backgroundColors.map((color) =>
                color.replace("rgb", "rgba").replace(")", ", 1)")
            );

            const ctx = canvas.getContext("2d");

            chartsRef.current[section.id] = new Chart(ctx, {
                type: section.chartType,

                data: {
                    labels: data.xValues,

                    datasets: [
                        {
                            label: `${section.fieldY} por ${section.fieldX}`,

                            data: data.yValues,

                            backgroundColor: backgroundColors,

                            borderColor: borderColors,

                            borderWidth: 1,
                        },
                    ],
                },

                options: {
                    responsive: true,

                    maintainAspectRatio: false,

                    scales: {
                        y: {
                            beginAtZero: true,

                            display: section.chartType !== "pie",
                        },

                        x: {
                            display: section.chartType !== "pie",

                            ticks: {
                                font: {
                                    size: 10,
                                },

                                autoSkip: false,

                                maxRotation: 60,

                                minRotation: 60,
                            },
                        },
                    },
                },
            });
        });
    }, [sections]);



    const generateRandomColors = (numColors) => {
        const colors = [];

        for (let i = 0; i < numColors; i++) {
            const r = Math.floor(Math.random() * 256);

            const g = Math.floor(Math.random() * 256);

            const b = Math.floor(Math.random() * 256);

            colors.push(`rgb(${r}, ${g}, ${b})`);
        }

        return colors;
    };

 

    const removeSection = (event) => {
        event.preventDefault();

        if (sections.length === 0) {
            return;
        }

        const lastSection = sections[sections.length - 1];

        const chart = chartsRef.current[lastSection.id];

        if (chart) {
            chart.destroy();

            delete chartsRef.current[lastSection.id];
        }

        setSections((previous) => previous.slice(0, -1));
    };



    useEffect(() => {
        if (!show) {
            Object.values(chartsRef.current).forEach((chart) => {
                chart?.destroy();
            });

            chartsRef.current = {};

            setSections([]);
        }
    }, [show]);



    useEffect(() => {
        return () => {
            Object.values(chartsRef.current).forEach((chart) => {
                chart?.destroy();
            });

            chartsRef.current = {};
        };
    }, []);



    const exportToPDF = (event) => {
        event.preventDefault();

        if (sections.length === 0) {
            return;
        }

        const pdf = new jsPDF();

        pdf.setFontSize(24);

        pdf.text("PEDEA - Relatório personalizado", 45, 15);

        let yOffset = 30;

        sections.forEach((section) => {
        

            if (yOffset > 260) {
                pdf.addPage();

                yOffset = 20;
            }

       
            if (section.title) {
                pdf.setFontSize(18);

                const titleLines = pdf.splitTextToSize(section.title, 180);

                pdf.text(titleLines, 10, yOffset);

                yOffset += titleLines.length * 8;
            }


            if (section.description) {
                pdf.setFontSize(11);

                const descriptionLines = pdf.splitTextToSize(
                    section.description,
                    180
                );

                pdf.text(descriptionLines, 10, yOffset);

                yOffset += descriptionLines.length * 6;
            }


            if (section.type === "groupBy" || section.type === "customQuery") {
                const xValues = section.data?.xValues || [];

                const yValues = section.data?.yValues || [];

                pdf.setFontSize(10);

                /*
                 * Cabeçalho
                 */

                pdf.text(String(section.fieldX), 10, yOffset);

                pdf.text(String(section.fieldY), 90, yOffset);

                yOffset += 7;

                xValues.forEach((x, index) => {
                    if (yOffset > 280) {
                        pdf.addPage();

                        yOffset = 20;
                    }

                    pdf.text(String(x ?? ""), 10, yOffset);

                    pdf.text(String(yValues[index] ?? ""), 90, yOffset);

                    yOffset += 6;
                });
            }

            /*
             * PERCENTUAL
             */

            if (section.type === "percentResult") {
                pdf.setFontSize(12);

                pdf.text(
                    `${(section.data?.percentage || 0).toFixed(
                        2
                    )}% dos registros atendem à condição especificada.`,
                    10,
                    yOffset
                );

                yOffset += 10;
            }

            /*
             * COUNT
             */

            if (section.type === "countResult") {
                pdf.setFontSize(12);

                pdf.text(
                    `Total de ${section.data?.count || 0
                    } registros que atendem à condição especificada.`,
                    10,
                    yOffset
                );

                yOffset += 10;
            }

            /*
             * MÉDIA
             */

            if (section.type === "averageResult") {
                pdf.setFontSize(12);

                pdf.text(
                    `A média simples de ${section.fieldZ} é ${(
                        section.data?.average || 0
                    ).toFixed(2)}.`,
                    10,
                    yOffset
                );

                yOffset += 10;
            }

            /*
             * GRÁFICO
             */

            if (section.type === "graphResult") {
                const canvas = document.getElementById(`chart-${section.id}`);

                if (canvas) {
                    const chartImage = canvas.toDataURL("image/png");

                    if (yOffset > 180) {
                        pdf.addPage();

                        yOffset = 20;
                    }

                    const canvasWidth = canvas.width;

                    const canvasHeight = canvas.height;

                    const pdfWidth = 180;

                    const pdfHeight = (canvasHeight / canvasWidth) * pdfWidth;

                    pdf.addImage(
                        chartImage,
                        "PNG",
                        10,
                        yOffset,
                        pdfWidth,
                        Math.min(pdfHeight, 100)
                    );

                    yOffset += Math.min(pdfHeight, 100) + 10;
                }
            }

            yOffset += 15;
        });

        pdf.save("storyTelling.pdf");
    };

    const dialogFooter = (
        <div className="StoryTellingDialogFooter">
            <Button
                onClick={handleClose}
                severity="secondary"
                className="BtnStoryFooter"
                icon="pi pi-times"
                raised
            >
                {t("common.close")}
            </Button>
        </div>
    );

    const renderSection = (section) => {
        if (section.status === "loading") {
            return (
                <div className="StorySectionLoading">
                    <div className="spinner-border"></div>

                    <span>{t("storyModal.loading")}</span>
                </div>
            );
        }

        if (section.status === "error") {
            return (
                <p className="StorySectionError">
                    Não foi possível carregar os dados desta seção.
                </p>
            );
        }

        if (section.type === "groupBy" || section.type === "customQuery") {
            const xValues = section.data?.xValues || [];

            const yValues = section.data?.yValues || [];

            if (xValues.length === 0) {
                return (
                    <p>
                        A consulta não retornou dados suficientes para
                        construir a seção desejada.
                    </p>
                );
            }

            return (
                <div className="StoryTableWrapper">
                    <table>
                        <thead>
                            <tr>
                                <th>{section.fieldX}</th>

                                <th>{section.fieldY}</th>
                            </tr>
                        </thead>

                        <tbody>
                            {xValues.map((x, index) => (
                                <tr key={`${section.id}-${index}`}>
                                    <td>{x ?? "Sem informação"}</td>

                                    <td>{yValues[index] ?? 0}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        }

        if (section.type === "percentResult") {
            return (
                <p>
                    {(section.data?.percentage || 0).toFixed(2)}% dos registros
                    atendem à condição especificada.
                </p>
            );
        }

        if (section.type === "countResult") {
            return (
                <p>
                    Total de <strong>{section.data?.count || 0}</strong>{" "}
                    registros que atendem à condição especificada.
                </p>
            );
        }

        if (section.type === "averageResult") {
            return (
                <p>
                    A média simples de <strong>{section.fieldZ}</strong>{" "}
                    considerando os registros que atendem à condição
                    especificada é{" "}
                    <strong>{(section.data?.average || 0).toFixed(2)}</strong>.
                </p>
            );
        }

        if (section.type === "graphResult") {
            const hasData = section.data?.xValues?.length > 0;

            if (!hasData) {
                return (
                    <p>
                        A consulta não retornou dados suficientes para
                        construir o gráfico.
                    </p>
                );
            }

            return (
                <div
                    className={
                        section.chartType === "pie"
                            ? "StoryChart StoryChartPie"
                            : "StoryChart"
                    }
                >
                    <canvas id={`chart-${section.id}`} />
                </div>
            );
        }

        return null;
    };

    return (
        <Dialog
            className="StoryTellingModal"
            header={t("storyModal.heading")}
            visible={show}
            onHide={handleClose}
            modal
            dismissableMask={false}
            closable
            draggable={false}
            resizable
            footer={dialogFooter}
            style={{
                width: "90vw",
                maxWidth: "1200px",
            }}
            contentStyle={{
                overflow: "auto",
            }}
        >
            <div className="containerStory">
                <h1>{t("storyModal.heading")}</h1>

                <form
                    id="StoryTellingModalCraftingForm"
                    className="StoryTellingModalCraftingForm"
                    onSubmit={addSection}
                >
                    <div className="StoryField">
                        <label htmlFor="layerName">
                            {t("storyModal.layerLabel")}
                        </label>

                        <Dropdown
                            inputId="layerName"
                            className="StoryDropdown"
                            panelClassName="StoryDropdownPanel"
                            value={layerName || null}
                            options={layerOptions}
                            onChange={(e) => loadAttributes(e.value || "")}
                            placeholder={t("storyModal.selectLayer")}
                            filter
                            filterPlaceholder="Buscar camada..."
                            emptyMessage="Nenhuma camada disponível"
                            emptyFilterMessage="Nenhuma camada encontrada"
                            showClear
                            appendTo={document.body}
                        />
                    </div>

                    <div className="StoryField">
                        <label htmlFor="sectionType">
                            {t("storyModal.sectionTypeLabel")}
                        </label>

                        <Dropdown
                            inputId="sectionType"
                            className="StoryDropdown"
                            panelClassName="StoryDropdownPanel"
                            value={sectionType}
                            options={sectionTypeOptions}
                            onChange={(e) => setSectionType(e.value)}
                            itemTemplate={iconItemTemplate}
                            valueTemplate={iconValueTemplate}
                            appendTo={document.body}
                        />
                    </div>

                    <div className="StoryField">
                        <label htmlFor="sectionTitle">
                            {t("storyModal.sectionTitle")}
                        </label>

                        <input
                            type="text"
                            className="form-control"
                            id="sectionTitle"
                            value={sectionTitle}
                            onChange={(event) =>
                                setSectionTitle(event.target.value)
                            }
                            placeholder={t("storyModal.sectionTitlePH")}
                        />
                    </div>

                    <div className="StoryField">
                        <label htmlFor="sectionDescription">
                            {t("storyModal.sectionDesc")}
                        </label>

                        <textarea
                            className="form-control"
                            id="sectionDescription"
                            rows="3"
                            value={sectionDescription}
                            onChange={(event) =>
                                setSectionDescription(event.target.value)
                            }
                            placeholder={t("storyModal.sectionDescPH")}
                        />
                    </div>

           

                    {showFieldX && (
                        <div id="fieldXSection" className="StoryField">
                            <label htmlFor="fieldX">
                                {t("statisticModal.xFieldLabel")}
                            </label>

                            <Dropdown
                                inputId="fieldX"
                                className="StoryDropdown"
                                panelClassName="StoryDropdownPanel"
                                value={fieldX || null}
                                options={fieldXOptions}
                                onChange={(e) => setFieldX(e.value || "")}
                                placeholder="Selecione um campo"
                                emptyMessage="Selecione uma camada primeiro"
                                filter={fieldXOptions.length > 8}
                                disabled={!layerName || loadingAttributes}
                                appendTo={document.body}
                            />
                        </div>
                    )}

                

                    {showFieldY && (
                        <div id="fieldYSection" className="StoryField">
                            <label htmlFor="fieldY">
                                {t("statisticModal.yFieldLabel")}
                            </label>

                            <Dropdown
                                inputId="fieldY"
                                className="StoryDropdown"
                                panelClassName="StoryDropdownPanel"
                                value={fieldY || null}
                                options={fieldYOptions}
                                onChange={(e) => setFieldY(e.value || "")}
                                placeholder="Selecione um campo"
                                emptyMessage="Selecione uma camada primeiro"
                                filter={fieldYOptions.length > 8}
                                disabled={!layerName || loadingAttributes}
                                appendTo={document.body}
                            />
                        </div>
                    )}

                    {/* CAMPO Z */}

                    {showFieldZ && (
                        <div
                            id="fieldZSection"
                            className="StoryField StoryFullField"
                        >
                            <label htmlFor="fieldZ">
                                {t("statisticModal.zFieldLabel")}
                            </label>

                            <Dropdown
                                inputId="fieldZ"
                                className="StoryDropdown"
                                panelClassName="StoryDropdownPanel"
                                value={fieldZ || null}
                                options={fieldZOptions}
                                onChange={(e) => setFieldZ(e.value || "")}
                                placeholder="Selecione um campo"
                                emptyMessage="Selecione uma camada primeiro"
                                filter={fieldZOptions.length > 8}
                                disabled={!layerName || loadingAttributes}
                                appendTo={document.body}
                            />
                        </div>
                    )}

              
                    {showOperator && (
                        <div
                            id="operatorSection"
                            className="StoryField StoryFullField"
                        >
                            <label htmlFor="operator">
                                {t("statisticModal.condition")}
                            </label>

                            <div className="StoryOperatorFields">
                                <Dropdown
                                    inputId="operator"
                                    className="StoryDropdown"
                                    panelClassName="StoryDropdownPanel"
                                    value={operator}
                                    options={operatorOptions}
                                    onChange={(e) => setOperator(e.value)}
                                    itemTemplate={operatorItemTemplate}
                                    valueTemplate={operatorValueTemplate}
                                    appendTo={document.body}
                                />

                                <input
                                    type="text"
                                    className="form-control"
                                    id="filterValue"
                                    value={filterValue}
                                    onChange={(event) =>
                                        setFilterValue(event.target.value)
                                    }
                                    placeholder="Informe o valor"
                                />
                            </div>
                        </div>
                    )}

               
                    {showChartType && (
                        <div
                            id="chartTypeSection"
                            className="StoryField StoryFullField"
                        >
                            <label htmlFor="chartType">
                                {t("statisticModal.chartTypeLabel")}
                            </label>

                            <Dropdown
                                inputId="chartType"
                                className="StoryDropdown"
                                panelClassName="StoryDropdownPanel"
                                value={chartType}
                                options={chartTypeOptions}
                                onChange={(e) => setChartType(e.value)}
                                itemTemplate={iconItemTemplate}
                                valueTemplate={iconValueTemplate}
                                appendTo={document.body}
                            />
                        </div>
                    )}

                    <div className="addSectionContainer">
                        <Button
                            type="submit"
                            id="addSection"
                            severity="info"
                            icon={
                                loadingSection
                                    ? "pi pi-spin pi-spinner"
                                    : "pi pi-plus-circle"
                            }
                            disabled={
                                loadingSection ||
                                loadingAttributes ||
                                !canAddSection
                            }
                            raised
                        >
                            {t("storyModal.btnAdd")}
                        </Button>

                        <Button
                            type="button"
                            id="exportSection"
                            severity="success"
                            icon="pi pi-file-export"
                            onClick={exportToPDF}
                            disabled={sections.length === 0}
                            raised
                        >
                            {t("storyModal.btnExport")}
                        </Button>

                        <Button
                            type="button"
                            id="removeSection"
                            severity="danger"
                            icon="pi pi-times"
                            onClick={removeSection}
                            disabled={sections.length === 0}
                            raised
                        >
                            {t("storyModal.btnRemove")}
                        </Button>
                    </div>
                </form>

                <hr />

                <div id="storyTellingSections" className="storyTellingSections">
                    {sections.map((section) => (
                        <div
                            key={section.id}
                            id={`section-${section.id}`}
                            className="section-container"
                        >
                            {section.title && <h3>{section.title}</h3>}

                            {section.description && (
                                <p className="section-description">
                                    {section.description}
                                </p>
                            )}

                            {renderSection(section)}
                        </div>
                    ))}
                </div>

                {(loadingAttributes || loadingSection) && (
                    <div id="loader" className="StoryLoader">
                        <div className="spinner-border" role="status" />

                        <span>{t("storyModal.loading")}</span>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default StoryTellingModal;